import { jwtDecode } from 'jwt-decode';

type JwtPayload = {
  'custom:vimond_entitlements'?: string;
  'https://vimond/entitlements'?: string;
  'https://vimond/svod'?: string;
  'https://vimond/tvod'?: string;
};

type TvodEntitlement = {
  a?: string[];
  c?: string[];
};

type Entitlement = {
  svod?: string | string[];
  tvod?: TvodEntitlement;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isNonEmptyStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => typeof item === 'string' && item.trim().length > 0);

const isTvodEntitlement = (value: unknown): value is TvodEntitlement => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const obj = value as Record<string, unknown>;
  const hasValidA = !('a' in obj) || isStringArray(obj.a);
  const hasValidC = !('c' in obj) || isStringArray(obj.c);

  // Must have at least one array property (can be empty)
  return hasValidA && hasValidC && ('a' in obj || 'c' in obj);
};

const isEntitlement = (value: unknown): value is Entitlement => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const obj = value as Record<string, unknown>;
  const hasSvod = 'svod' in obj;
  const hasTvod = 'tvod' in obj;

  // Must have at least one property
  if (!hasSvod && !hasTvod) {
    return false;
  }

  // Validate svod if present
  if (hasSvod) {
    const svod = obj.svod;
    const isValidSvod =
      (typeof svod === 'string' && svod.trim().length > 0) ||
      isNonEmptyStringArray(svod);
    if (!isValidSvod) {
      return false;
    }
  }

  // Validate tvod if present
  if (hasTvod && !isTvodEntitlement(obj.tvod)) {
    return false;
  }

  return true;
};

const extractProductIds = (
  svod?: string | string[],
  tvod?: TvodEntitlement,
): string[] => {
  const ids: string[] = [];

  // Collect SVOD IDs
  if (svod) {
    const svodArray = Array.isArray(svod) ? svod : [svod];
    ids.push(
      ...svodArray.filter(
        (id) => typeof id === 'string' && id.trim().length > 0,
      ),
    );
  }

  // Collect TVOD IDs
  if (tvod) {
    const tvodArrays = [tvod.a, tvod.c].filter(isNonEmptyStringArray);
    ids.push(...tvodArrays.flat());
  }

  return ids;
};

const parseNewEntitlementsFormat = (entitlementsClaim: string): Set<string> => {
  const productIds = new Set<string>();

  try {
    const parsed = JSON.parse(entitlementsClaim);
    if (!Array.isArray(parsed)) {
      return productIds;
    }

    parsed.forEach((item) => {
      if (!isEntitlement(item)) {
        return;
      }

      const ids = extractProductIds(item.svod, item.tvod);
      ids.forEach((id) => productIds.add(id));
    });
  } catch {
    console.info(
      'Invalid JSON in entitlements claim, falling back to legacy format (https://vimond/svod and https://vimond/tvod)',
    );
  }

  return productIds;
};

const parseLegacyFormat = (claims: JwtPayload): Set<string> => {
  const productIds = new Set<string>();

  // Parse SVOD claims
  const svod = claims['https://vimond/svod'];
  if (svod && typeof svod === 'string') {
    svod
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0)
      .forEach((id) => productIds.add(id));
  }

  // Parse TVOD claims
  const tvod = claims['https://vimond/tvod'];
  if (tvod && typeof tvod === 'string') {
    tvod
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0)
      .forEach((id) => productIds.add(id));
  }

  return productIds;
};

export const getProductIds = (token: string): Set<string> => {
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    return new Set<string>();
  }

  try {
    const claims = jwtDecode<JwtPayload>(token);
    if (!claims || typeof claims !== 'object') {
      return new Set<string>();
    }

    // Try new entitlements format first
    const entitlementsClaim = claims['https://vimond/entitlements'];
    if (entitlementsClaim && typeof entitlementsClaim === 'string') {
      return parseNewEntitlementsFormat(entitlementsClaim);
    }

    // Fallback to legacy format
    return parseLegacyFormat(claims);
  } catch (e: unknown) {
    console.error('Error decoding JWT token:', e);
    return new Set<string>();
  }
};
